import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Paper,
  Grid
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import {
  BalanceSheetData,
  BalanceSheetPrimaryGroup,
  BalanceSheetSubGroup,
  BalanceSheetLedger
} from '../../utils/report.types';

import { Utils } from '../../utils/common-utils';
interface BalanceSheetProps {
  data: BalanceSheetData | null;
  loading?: boolean;
  error?: string;
}

const BalanceSheet: React.FC<BalanceSheetProps> = ({ data, loading, error }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSubGroups, setExpandedSubGroups] = useState<Set<string>>(new Set());
  const [expandedLedgers, setExpandedLedgers] = useState<Set<string>>(new Set());

  const formatAmount = (amount: number) => Utils.formatNumber(Math.abs(Number(amount) || 0), true, true);

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleSubGroup = (subGroupId: string) => {
    const newExpanded = new Set(expandedSubGroups);
    if (newExpanded.has(subGroupId)) {
      newExpanded.delete(subGroupId);
    } else {
      newExpanded.add(subGroupId);
    }
    setExpandedSubGroups(newExpanded);
  };

  const toggleLedger = (ledgerId: string) => {
    const newExpanded = new Set(expandedLedgers);
    if (newExpanded.has(ledgerId)) {
      newExpanded.delete(ledgerId);
    } else {
      newExpanded.add(ledgerId);
    }
    setExpandedLedgers(newExpanded);
  };

  const renderLedgerDetails = (ledger: BalanceSheetLedger) => {
    const isExpanded = expandedLedgers.has(ledger._id);

    return (
      <>
        <TableRow
          hover
          onClick={() => toggleLedger(ledger._id)}
          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f9f9f9' } }}
        >
          <TableCell sx={{ pl: 6, py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small" sx={{ padding: 0, width: 20, height: 20 }}>
                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </IconButton>
              <Typography sx={{ fontSize: '0.813rem' }}>{ledger.name}</Typography>
              <Chip label={ledger.accountingCode} size="small" sx={{ height: 16, fontSize: '0.65rem', ml: 0.5 }} />
            </Box>
          </TableCell>
          <TableCell align="right" sx={{ py: 0.75, pr: 2, fontSize: '0.813rem', fontWeight: 500, borderBottom: '1px solid #e0e0e0' }}>
            {formatAmount(ledger.balance)}
          </TableCell>
        </TableRow>
        {isExpanded && (
          <TableRow>
            <TableCell colSpan={2} sx={{ p: 0, border: 'none', backgroundColor: '#fafafa' }}>
              <Box sx={{ px: 8, py: 1 }}>
                <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Debit</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{formatAmount(ledger.debit)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Credit</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{formatAmount(ledger.credit)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Balance</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{formatAmount(ledger.balance)}</Typography>
                  </Box>
                </Box>
                {ledger.parties && ledger.parties.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5, display: 'block' }}>
                      Party-wise Breakdown
                    </Typography>
                    <Table size="small" sx={{ width: 'auto' }}>
                      <TableBody>
                        {ledger.parties.map((party) => (
                          <TableRow key={party._id} sx={{ '& td': { py: 0.25, fontSize: '0.75rem', border: 'none' } }}>
                            <TableCell>{party.name}</TableCell>
                            <TableCell align="right" sx={{ pl: 3 }}>{formatAmount(party.balance)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  const renderLedgers = (ledgers: BalanceSheetLedger[]) => {
    if (!ledgers || ledgers.length === 0) return null;
    return ledgers.map((ledger) => renderLedgerDetails(ledger));
  };

  const renderSubGroups = (subGroups: BalanceSheetSubGroup[]) => {
    if (!subGroups || subGroups.length === 0) return null;
    return subGroups.map((subGroup) => {
      const isExpanded = expandedSubGroups.has(subGroup._id);
      return (
        <React.Fragment key={subGroup._id}>
          <TableRow
            hover
            onClick={() => toggleSubGroup(subGroup._id)}
            sx={{ cursor: 'pointer', backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#eeeeee' } }}
          >
            <TableCell sx={{ pl: 4, py: 0.75, fontSize: '0.813rem', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" sx={{ padding: 0, width: 20, height: 20 }}>
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                </IconButton>
                <Typography sx={{ fontSize: '0.813rem', fontWeight: 600 }}>{subGroup.name}</Typography>
                <Chip label={subGroup.accountingCode} size="small" sx={{ height: 16, fontSize: '0.65rem', ml: 0.5 }} />
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ py: 0.75, pr: 2, fontSize: '0.813rem', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
              {formatAmount(subGroup.total)}
            </TableCell>
          </TableRow>
          {isExpanded && (
            <TableRow>
              <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                <Table size="small">
                  <TableBody>
                    {renderLedgers(subGroup.ledgers)}
                  </TableBody>
                </Table>
              </TableCell>
            </TableRow>
          )}
        </React.Fragment>
      );
    });
  };

  const renderPrimaryGroups = (groups: BalanceSheetPrimaryGroup[]) => {
    if (!groups || groups.length === 0) return null;
    return groups.map((group) => {
      const isExpanded = expandedGroups.has(group._id);
      return (
        <React.Fragment key={group._id}>
          <TableRow
            hover
            onClick={() => toggleGroup(group._id)}
            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f0f0f0' } }}
          >
            <TableCell sx={{ pl: 2, py: 1, fontSize: '0.875rem', fontWeight: 700, borderBottom: '1px solid #d0d0d0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" sx={{ padding: 0, width: 20, height: 20 }}>
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                </IconButton>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>{group.name}</Typography>
                <Chip label={group.accountingCode} size="small" sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }} />
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ py: 1, pr: 2, fontSize: '0.875rem', fontWeight: 700, borderBottom: '1px solid #d0d0d0' }}>
              {formatAmount(group.total)}
            </TableCell>
          </TableRow>
          {isExpanded && (
            <TableRow>
              <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                <Table size="small">
                  <TableBody>
                    {group.subGroups && renderSubGroups(group.subGroups)}
                    {group.ledgers && renderLedgers(group.ledgers)}
                  </TableBody>
                </Table>
              </TableCell>
            </TableRow>
          )}
        </React.Fragment>
      );
    });
  };

  const renderRevenueExpenseBreakdown = (groups: BalanceSheetPrimaryGroup[], type: 'revenue' | 'expense') => {
    if (!groups || groups.length === 0) return null;
    return groups.map((group) => {
      const isExpanded = expandedGroups.has(group._id);
      return (
        <React.Fragment key={group._id}>
          <TableRow
            hover
            onClick={() => toggleGroup(group._id)}
            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f9f9f9' } }}
          >
            <TableCell sx={{ pl: 3, py: 0.75, fontSize: '0.813rem', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" sx={{ padding: 0, width: 20, height: 20 }}>
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                </IconButton>
                <Typography sx={{ fontSize: '0.813rem', fontWeight: 600 }}>{group.name}</Typography>
                <Chip label={group.accountingCode} size="small" sx={{ height: 16, fontSize: '0.65rem', ml: 0.5 }} />
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ py: 0.75, pr: 2, fontSize: '0.813rem', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
              {formatAmount(group.total)}
            </TableCell>
          </TableRow>
          {isExpanded && (
            <TableRow>
              <TableCell colSpan={2} sx={{ p: 0, border: 'none', backgroundColor: '#fafafa' }}>
                <Table size="small">
                  <TableBody>
                    {group.subGroups && group.subGroups.map((subGroup) => (
                      <React.Fragment key={subGroup._id}>
                        <TableRow>
                          <TableCell sx={{ pl: 5, py: 0.5, fontSize: '0.75rem', fontWeight: 600, border: 'none' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{subGroup.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, pr: 2, fontSize: '0.75rem', fontWeight: 600, border: 'none' }}>
                            {formatAmount(Math.abs(subGroup.total))}
                          </TableCell>
                        </TableRow>
                        {subGroup.ledgers && subGroup.ledgers.map((ledger) => (
                          <TableRow key={ledger._id}>
                            <TableCell sx={{ pl: 7, py: 0.5, fontSize: '0.75rem', border: 'none' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography sx={{ fontSize: '0.75rem' }}>{ledger.name}</Typography>
                                <Chip label={ledger.accountingCode} size="small" sx={{ height: 14, fontSize: '0.6rem' }} />
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.5, pr: 2, fontSize: '0.75rem', border: 'none' }}>
                              {formatAmount(Math.abs(ledger.balance))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                    {group.ledgers && group.ledgers.map((ledger) => (
                      <TableRow key={ledger._id}>
                        <TableCell sx={{ pl: 6, py: 0.5, fontSize: '0.75rem', border: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '0.75rem' }}>{ledger.name}</Typography>
                            <Chip label={ledger.accountingCode} size="small" sx={{ height: 14, fontSize: '0.6rem' }} />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, pr: 2, fontSize: '0.75rem', border: 'none' }}>
                          {formatAmount(Math.abs(ledger.balance))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableCell>
            </TableRow>
          )}
        </React.Fragment>
      );
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No data available. Please select a date range and generate the report.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 1, backgroundColor: '#fff' }}>
      {/* Two Column Layout */}
      <Grid container spacing={2}>
        {/* Left Column - Assets */}
        <Grid item xs={12} md={6}>
          {/* ASSETS Section */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#eaecf0' }}>
                  <TableCell sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem', py: 1 }}>
                    ASSETS
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem', py: 1 }}>
                    {formatAmount(data.assets.total)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderPrimaryGroups(data.assets.groups)}
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #1976d2' }}>
                    TOTAL ASSETS
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #1976d2' }}>
                    {formatAmount(data.assets.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Right Column - Equity & Liabilities */}
        <Grid item xs={12} md={6}>
          {/* EQUITY Section */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#eaecf0' }}>
                  <TableCell sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem', py: 1 }}>
                    EQUITY
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem', py: 1 }}>
                    {formatAmount(data.equity.total)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Capital Account & Reserves Groups (BA, BB) */}
                {data.equity.groups && renderPrimaryGroups(data.equity.groups)}

                {/* Profit & Loss for the Period */}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '0.813rem', py: 0.75, borderTop: '1px solid #d0d0d0' }}>
                    Profit & Loss for the Period
                  </TableCell>
                </TableRow>

                {/* Revenue */}
                <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                  <TableCell sx={{ pl: 3, fontWeight: 700, fontSize: '0.813rem', py: 0.75 }}>
                    Revenue
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.813rem', py: 0.75 }}>
                    {formatAmount(data.equity.revenue)}
                  </TableCell>
                </TableRow>
                {data.equity.breakdown?.revenue && renderRevenueExpenseBreakdown(data.equity.breakdown.revenue, 'revenue')}

                {/* Expenses */}
                <TableRow sx={{ backgroundColor: '#ffebee' }}>
                  <TableCell sx={{ pl: 3, fontWeight: 700, fontSize: '0.813rem', py: 0.75 }}>
                    Expenses
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.813rem', py: 0.75 }}>
                    {formatAmount(data.equity.expenses)}
                  </TableCell>
                </TableRow>
                {data.equity.breakdown?.expenses && renderRevenueExpenseBreakdown(data.equity.breakdown.expenses, 'expense')}

                {/* Round Off */}
                {data.equity.roundOff && (
                  <>
                    <TableRow>
                      <TableCell sx={{ pl: 4, py: 0.5, fontSize: '0.75rem', borderBottom: '1px solid #e0e0e0' }}>
                        Round Off Income
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, pr: 2, fontSize: '0.75rem', borderBottom: '1px solid #e0e0e0' }}>
                        {formatAmount(data.equity.roundOff.roundOffIncome.total)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4, py: 0.5, fontSize: '0.75rem', borderBottom: '1px solid #e0e0e0' }}>
                        Round Off Expense
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, pr: 2, fontSize: '0.75rem', borderBottom: '1px solid #e0e0e0' }}>
                        {formatAmount(data.equity.roundOff.roundOffExpense.total)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4, py: 0.5, fontSize: '0.75rem', fontWeight: 600, borderBottom: '1px solid #d0d0d0' }}>
                        Net Round Off
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, pr: 2, fontSize: '0.75rem', fontWeight: 600, borderBottom: '1px solid #d0d0d0' }}>
                        {formatAmount(data.equity.roundOff.netRoundOff)}
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Net Profit/Loss */}
                <TableRow sx={{ backgroundColor: data.equity.netProfit >= 0 ? '#e8f5e9' : '#ffebee' }}>
                  <TableCell sx={{ pl: 3, fontWeight: 700, fontSize: '0.813rem', py: 0.75, borderTop: '1px solid #388e3c' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.813rem' }}>
                        Net {data.equity.netProfit >= 0 ? 'Profit' : 'Loss'}
                      </Typography>
                      <Chip
                        label={data.equity.netProfit >= 0 ? 'Profit' : 'Loss'}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          backgroundColor: data.equity.netProfit >= 0 ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.813rem',
                      py: 0.75,
                      borderTop: '1px solid #388e3c',
                      color: data.equity.netProfit >= 0 ? '#2e7d32' : '#c62828'
                    }}
                  >
                    {formatAmount(data.equity.netProfit)}
                  </TableCell>
                </TableRow>

                {/* Total Equity */}
                <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #388e3c' }}>
                    TOTAL EQUITY
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #388e3c' }}>
                    {formatAmount(data.equity.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* LIABILITIES Section */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#eaecf0' }}>
                  <TableCell sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem', py: 1 }}>
                    LIABILITIES
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem', py: 1 }}>
                    {formatAmount(data.liabilities.total)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderPrimaryGroups(data.liabilities.groups)}
                <TableRow sx={{ backgroundColor: '#ffebee' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #d32f2f' }}>
                    TOTAL LIABILITIES
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #d32f2f' }}>
                    {formatAmount(data.liabilities.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Balance Verification Footer */}
      <Box sx={{ mt: 2, p: 1.5, backgroundColor: data.balanceCheck.isBalanced ? '#e8f5e9' : '#ffebee', border: `2px solid ${data.balanceCheck.isBalanced ? '#4caf50' : '#f44336'}` }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: data.balanceCheck.isBalanced ? '#2e7d32' : '#c62828' }}>
              {data.balanceCheck.isBalanced ? '✓ Balance Sheet is Balanced' : '✗ Balance Sheet Mismatch'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.balanceCheck.formula}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Assets</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatAmount(data.balanceCheck.leftSide)}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Liabilities + Equity</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatAmount(data.balanceCheck.rightSide)}</Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BalanceSheet;
